import React, { FC, useEffect, useState } from "react";
import { FormSectionDto } from "@green0meter/api-client";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { SortableContainer, SortableElement } from "react-sortable-hoc";
import { useTranslation } from "react-i18next";
import { arrayMoveImmutable } from "array-move";

import { Drawer } from "@components/common";
import { Box, Button, Card, Stack } from "@components/base";
import { useSortFormSections } from "@api";
import { useUserStore } from "@store/user";

interface SortSections {
  formId: string;
  formSections: FormSectionDto[];
  open: boolean;
  onClose: () => void;
}

const SortableItem = SortableElement<{ value: FormSectionDto }>(
  ({ value }: { value: FormSectionDto }) => (
    <Card sx={{ zIndex: 99999, userSelect: "none" }}>
      <Stack spacing={1} direction="row" alignItems="center" p={1}>
        <DragIndicatorIcon />
        <Box flexGrow={1}>{value.name}</Box>
      </Stack>
    </Card>
  ),
);

const SortableList = SortableContainer<{ items: FormSectionDto[] }>(
  ({ items }: { items: FormSectionDto[] }) => {
    return (
      <Stack spacing={1}>
        {items.map((value, index) => (
          <SortableItem key={`item-${index}`} index={index} value={value} />
        ))}
      </Stack>
    );
  },
);

const SortSections: FC<SortSections> = ({ formId, open, onClose, formSections }) => {
  const { t } = useTranslation();
  const [sections, setSections] = useState(formSections);
  const { user } = useUserStore();
  const {
    sortFormSectionsAsync,
    sortFormSectionsEnabled,
    isPendingSortFormSections,
  } = useSortFormSections(user, formId);

  const handleSaveSortSections = async (): Promise<void> => {
    sortFormSectionsAsync({
      formId,
      dto: {
        sectionIds: sections.map((s) => s.id),
      },
    });
    onClose();
  };

  useEffect(() => {
    setSections(formSections);
  }, [formSections, open]);

  return (
    sortFormSectionsEnabled && (
      <Drawer
        open={open}
        onClose={onClose}
        title={t("multistep.sortSections")}
        content={
          <Stack spacing={2}>
            <Card color="grey">
              <Box p={2} sx={{ cursor: "move" }}>
                <SortableList
                  items={sections}
                  onSortEnd={({ oldIndex, newIndex }) =>
                    setSections(arrayMoveImmutable(sections, oldIndex, newIndex))
                  }
                />
              </Box>
            </Card>
            <Stack direction="row" justifyContent="space-between">
              <Button variant="outlined" onClick={() => onClose()}>
                {t("common.cancel")}
              </Button>
              <Button
                onClick={handleSaveSortSections}
                isLoading={isPendingSortFormSections}
              >
                {t("multistep.saveSortSections")}
              </Button>
            </Stack>
          </Stack>
        }
      />
    )
  );
};

export default SortSections;
